import {
    RealtimeEventData,
    SecurityAlertMedia,
    SecurityAlertResolveInitiated,
    // SecurityAlertResolved,
    useSecurityAlertsNotifications,
} from "@lvt/security-alerts-notification-service-client";
import { useViewNotification } from "@lvt/view-notification-service-client";
import { Box } from "@material-ui/core";
import { Alert as ToastAlert } from "@material-ui/lab";
import { Zoom } from "@mui/material";
import { useSnackbar } from "notistack";
import queryString from "query-string";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { useLiveUnitMosaicDataQuery } from "@/api/liveUnitMosaic/liveUnitMosaic";
import { AlertService } from "@/api/services/interfaces";
import { AlertMediaTile } from "@/common/components/liveUnitMosaic/AlertMediaTile/AlertMediaTile";
import CameraStreamTile from "@/common/components/liveUnitMosaic/CameraStreamTile/CameraStreamTile";
import { AlertTimelineFooter } from "@/common/components/liveUnitMosaic/Footer/AlertTimelineFooter/AlertTimelineFooter";
import { LiveStreamFooter } from "@/common/components/liveUnitMosaic/Footer/LiveStreamFooter/LiveStreamFooter";
import Header from "@/common/components/liveUnitMosaic/Header/Header";
import { Mosaic } from "@/common/components/liveUnitMosaic/Mosaic/Mosaic";
import { Loading } from "@/common/containers/Loading";
import { AlertTileContext } from "@/common/contexts/AlertTileContextType";
import { useDispatch, useSelector } from "@/store";
import { addAlert, Severity } from "@/store/alerts/slice";
import { groupAlertsByTimestampCameraId } from "@/utilities/alerts";
import { Alert, raisedNotificationToMedia, TimestampCameraMedia } from "@/utilities/types/alert";
import { LiveUnitMosaicData } from "@/utilities/types/mosaicData";
import { Viewer } from "@/utilities/types/viewer";

import { DEFAULT_INITIAL_CONNECTION_TIME } from "../alerts/AlertsPage/AlertsPage";
import useStyles from "./LiveUnitMosaicPage.styles";
import { LiveUnitViewers } from "./LiveUnitViewers/LiveUnitViewers";


export interface LiveUnitMosaicPageParams {
    liveUnitId: string;
}

export interface PageParams {
    contextId: string;
    alertId: string;
}

interface LiveUnitMosaicPageProps {
    alertService: AlertService,
    /**
     * Determines how long to wait before showing the alert banner in ms (if the service still hasn't connected)
     */
    initialConnectionTime?: number;
}

export type SetLiveUnitMosaicData = (value: (
    ((prevState: LiveUnitMosaicData | undefined) => LiveUnitMosaicData | undefined)
    | LiveUnitMosaicData
    | undefined
)) => void;

const LiveUnitMosaicPage: FC<LiveUnitMosaicPageProps> = ({ alertService, initialConnectionTime = DEFAULT_INITIAL_CONNECTION_TIME }: LiveUnitMosaicPageProps) => {
    const { liveUnitId: liveUnitIdString } = useParams<LiveUnitMosaicPageParams>();
    const liveUnitId = parseInt(liveUnitIdString, 10); // Parse in base 10

    const urlLocation = useLocation();
    const { t } = useTranslation("translation", { keyPrefix: "components.liveUnitMosaicPage" });
    const user = useSelector(state => state.user);

    const { data: mosaicData, isSuccess, error: getMosaicDataError } = useLiveUnitMosaicDataQuery(liveUnitId);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    //DISCUSS: Do we need the concept of contextId and alertId now that we know there's no timeline and we wouldnt be viewing any other alert (alert with diff id) from a current alert?

    const [alert, setAlert] = useState<Alert | undefined>(undefined);
    const [isLive, setIsLive] = useState(true);

    const dispatch = useDispatch();
    const classes = useStyles();

    // Changes the media data sent to the alerts clips mosaic component and also shows the currently selected timestamp
    const [currentAlertTimestamp, setCurrentAlertTimestamp] = useState<string>("");
    const [shouldClipsPlay, setShouldClipsPlay] = useState<boolean>(true);
    const [clipProgress, setClipProgress] = useState(0);
    const [isSeeking, setIsSeeking] = useState<boolean>(false);

    const [liveUnitViewers] = useViewNotification("live-unit", liveUnitIdString);

    const [viewers, setViewers] = useState<Viewer[]>([]);
    const getIsViewOnly = () => {
        // If the user doesn't exist from the store yet the user is view-only
        if (!user) return true;

        // If the user doesn't have a name the user is view-only
        if (!user.name) return true;

        // If there are other viewers the user is view-only
        if (viewers.length) {
            // If the current user's name is the same as the first viewer's name they are not view-only, return false
            // else the user is view-only
            return user.name !== viewers[0].name;
        } else {
            // If the user is the only viewer they are not view-only
            return false;
        }
    };
    const isViewOnly = getIsViewOnly();

    const alertId = alert?.alertId ? `${liveUnitIdString}.${alert.alertId}` : undefined;

    useViewNotification("alert", alertId);

    const queryParsed = queryString.parse(urlLocation.search) as unknown as PageParams;

    // Flag representing if the initial connection time has expired
    const [isInitialConnection, setIsInitialConnection] = useState(true);

    const [securityAlertsCallbacks, securityAlertsConnectionState] = useSecurityAlertsNotifications();

    useEffect(() => {
        const updateViewers = () => {
            const luViewers: Viewer[] = liveUnitViewers.map(v => ({
                ...v,
                joined: new Date(v.joined),
                viewOnlyAccess: true,
            }));

            if (luViewers.length) luViewers[0].viewOnlyAccess = false;

            luViewers.sort((a, b) => a.joined.valueOf() - b.joined.valueOf());

            setViewers(luViewers);
        };

        updateViewers();
    }, [liveUnitViewers]);

    useEffect(() => {
        alertService.getAlert(queryParsed.contextId).then((res) => {
            setAlert(res);
        }).catch(() => {
            dispatch(
                addAlert({
                    severity: Severity.Error,
                    message: t("errors.getAlert"),
                }),
            );
        });
    }, [queryParsed.contextId]);

    const groupedAlertMedia: TimestampCameraMedia[] | undefined = useMemo(() => {
        if (alert) {
            return groupAlertsByTimestampCameraId(alert.media);
        }
    }, [alert]);

    //Set first alert as primary alert. This can also be updated to be sent by the backend.
    const primaryAlertTimestamp = (groupedAlertMedia && groupedAlertMedia.length !== 0) ? groupedAlertMedia[0].timestamp : "";

    useEffect(() => {
        if (groupedAlertMedia?.length) {
            // Set current alert as the first)
            setCurrentAlertTimestamp(groupedAlertMedia[0].timestamp);
        }
    }, [groupedAlertMedia]);

    useEffect(() => {
        if (getMosaicDataError) {
            dispatch(
                addAlert({
                    severity: Severity.Error,
                    message: t("errors.getLiveUnitStreamData"),
                }),
            );
        }
    }, [getMosaicDataError]);

    useEffect(() => {
        if (queryParsed?.contextId) {
            if (queryParsed?.alertId) {
                setIsLive(false);
            }
        }
    }, [queryParsed]);

    useEffect(() => {
        const registerHandlers = () => {
            securityAlertsCallbacks.onSecurityAlertMediaAvailable((media: RealtimeEventData<SecurityAlertMedia>) => {
                setAlert((previousAlert) => {
                    if (previousAlert && previousAlert.alertId === media.payload.alertId) {
                        previousAlert.media.push(raisedNotificationToMedia(media.payload));

                        return { ...previousAlert };
                    }

                    return previousAlert;
                });
            });

            // For the UI, an alert can be removed from the list when resolution has been initiated.
            securityAlertsCallbacks.onSecurityAlertResolveInitiated((resolveInitiated: RealtimeEventData<SecurityAlertResolveInitiated>) => {
                setAlert((previousAlert) => {
                    if (previousAlert && previousAlert.alertId === resolveInitiated.payload.alertId) {
                        return { ...previousAlert, state: "resolved" };
                    }

                    return previousAlert;
                });
            });

            // TODO: Use this after mvp to show alert resolution notification
            // securityAlertsCallbacks.onSecurityAlertResolved((resolved: RealtimeEventData<SecurityAlertResolved>) => {});
        };

        registerHandlers();
    }, []);

    // Set a timeout to prevent the disconnect banner from showing up during initial connection
    useEffect(() => {
        const timeoutId = setTimeout(() => setIsInitialConnection(false), initialConnectionTime);

        return () => clearTimeout(timeoutId);
    }, []);

    //Get data with the selected timestamp for the Alerts grid
    const camAlertMediaList = useMemo(() => {
        const matchList = groupedAlertMedia?.filter((ele) => ele.timestamp === currentAlertTimestamp) || [];

        if (matchList.length === 0) {
            return [];
        }

        return matchList[0].cameraMediaList;
    }, [currentAlertTimestamp, groupedAlertMedia]);

    useEffect(() => {
        if (securityAlertsConnectionState === "disconnected" && !isInitialConnection) {
            const alert = (
                <ToastAlert key="disconnected-alert-message" severity="error">
                    {t("errors.realtimeEvents")}
                </ToastAlert>
            );

            const key = enqueueSnackbar(alert, {
                anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "right",
                },
                autoHideDuration: 8_000,
                key: "disconnected-alert-message",
                onClick: () => closeSnackbar(key),
                preventDuplicate: true,
                variant: Severity.Error,
            });
        }
    }, [securityAlertsConnectionState, isInitialConnection]);

    const mosaicHeight = "calc(100vh - 68px - 76px)"; // view - header - footer
    const history = useHistory();

    if (alert && alert.state === "resolved") history.push("/alerts");

    return (
        <AlertTileContext.Provider value={{
            shouldClipsPlay,
            setShouldClipsPlay,
            clipProgress,
            setClipProgress,
            isSeeking,
            setIsSeeking,
            primaryAlertTimestamp,
            currentAlertTimestamp,
            setCurrentAlertTimestamp,
        }}>
            <div className={classes.page}>
                <Header title={mosaicData && `${mosaicData.cameras[0].locationName}, ${mosaicData.cameras[0].liveUnitName}`}>
                    <Zoom in={viewers.length > 1}>
                        <Box> {/* Zoom requires this box to have a ref to hold on to */}
                            {viewers.length > 1 && <LiveUnitViewers viewers={viewers} displayViewOnly={isViewOnly} />}
                        </Box>
                    </Zoom>
                </Header>
                {isLive ?
                    <>
                        <div className={classes.mosaic} style={{ height: mosaicHeight }}>
                            {mosaicData && isSuccess ?
                                <Mosaic data={mosaicData.cameras} TileComponent={CameraStreamTile} tileProps={{ hideControls: isViewOnly }} />
                                :
                                // TODO: add case for isError for useLiveUnitMosaicDataQuery
                                <Loading />
                            }
                        </div>
                        <LiveStreamFooter
                            mosaicData={mosaicData}
                            alertId={queryParsed.contextId}
                            setIsLive={setIsLive}
                            hideControls={isViewOnly}
                            allowLights={
                                !!mosaicData?.cameras[0].capabilities.relayActions.length &&
                                mosaicData?.userPermissions.canControlPtzf
                            }
                            allowSounds={
                                !!mosaicData?.cameras[0].capabilities.speakerSounds.length &&
                                mosaicData?.userPermissions.canAccessQuickSounds
                            }
                            allowSpeakerTalkdown={
                                mosaicData?.cameras[0].capabilities.speakerTalkdown &&
                                mosaicData?.userPermissions.canSpeakerTalkdown
                            }
                            disableGoToAlertsButton={alert === undefined || (alert && alert.media.length === 0)}
                        />
                    </>
                    :
                    <>
                        {alert ? <>
                            <div
                                data-testid="alert-media-tile-wrapper"
                                className={classes.mosaic} style={{ height: mosaicHeight }}>
                                {groupedAlertMedia ?
                                    <Mosaic data={camAlertMediaList} TileComponent={AlertMediaTile} />
                                    :
                                    <Loading />
                                }
                            </div>
                            <AlertTimelineFooter
                                contextId={queryParsed.contextId}
                                groupedAlertMedia={groupedAlertMedia}
                                alert={alert}
                                setIsLive={setIsLive}
                            />
                        </> : <Loading />}
                    </>
                }
            </div >
        </AlertTileContext.Provider >
    );
};

export default LiveUnitMosaicPage;
